import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Input } from '../../components/ui/input';
import { 
  Bell, Clock, AlertCircle, Send, Mail, CheckCircle, 
  Calendar, ChevronRight, Settings, History, RefreshCw,
  ArrowLeft, Plus, Trash2, Save
} from 'lucide-react';
import { Link } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RemindersPage = () => {
  const [config, setConfig] = useState(null);
  const [overdueInvoices, setOverdueInvoices] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sendingReminder, setSendingReminder] = useState(null);
  const [schedule, setSchedule] = useState([7, 14, 30]);
  const [newDay, setNewDay] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };

      const [configRes, overdueRes, statsRes] = await Promise.all([
        fetch(`${API_URL}/api/reminders/config`, { headers }),
        fetch(`${API_URL}/api/reminders/overdue`, { headers }),
        fetch(`${API_URL}/api/reminders/stats`, { headers })
      ]);

      const configData = await configRes.json();
      const overdueData = await overdueRes.json();
      const statsData = await statsRes.json();

      setConfig(configData.config);
      setSchedule(configData.config?.schedule || [7, 14, 30]);
      setOverdueInvoices(overdueData.overdue_invoices || []);
      setStats(statsData);
    } catch (error) {
      console.error('Erreur chargement données:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/api/reminders/config`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          enabled: config.enabled,
          schedule: schedule,
          send_email: config.send_email
        })
      });
      await loadData();
    } catch (error) {
      console.error('Erreur sauvegarde:', error);
    } finally {
      setSaving(false);
    }
  };

  const sendReminder = async (invoiceId, reminderType) => {
    setSendingReminder(invoiceId);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/reminders/send`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          invoice_id: invoiceId,
          reminder_type: reminderType
        })
      });
      
      if (res.ok) {
        await loadData();
      }
    } catch (error) {
      console.error('Erreur envoi:', error);
    } finally {
      setSendingReminder(null);
    }
  };

  const addScheduleDay = () => {
    const day = parseInt(newDay);
    if (day > 0 && !schedule.includes(day)) {
      setSchedule([...schedule, day].sort((a, b) => a - b));
      setNewDay('');
    }
  };

  const removeScheduleDay = (day) => {
    setSchedule(schedule.filter(d => d !== day));
  };

  const getReminderLabel = (count) => {
    if (count === 0) return 'first';
    if (count === 1) return 'second';
    return 'third';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="reminders-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/dashboard/smartfacture">
            <Button variant="ghost" size="sm" className="text-slate-500 hover:text-slate-700">
              <ArrowLeft className="h-4 w-4 mr-1" />
              Retour
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Relances automatiques</h1>
            <p className="text-slate-500 text-sm">Gérez les rappels pour vos factures impayées</p>
          </div>
        </div>
        <Button 
          onClick={saveConfig} 
          disabled={saving}
          className="bg-blue-600 hover:bg-blue-700"
          data-testid="save-config-btn"
        >
          {saving ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
          Enregistrer
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card className="border-slate-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Factures en retard</p>
                <p className="text-2xl font-bold text-red-600" data-testid="overdue-count">
                  {stats?.overdue_invoices || 0}
                </p>
              </div>
              <div className="p-3 rounded-xl bg-red-50">
                <AlertCircle className="h-5 w-5 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Montant en retard</p>
                <p className="text-2xl font-bold text-amber-600">
                  {stats?.total_overdue_amount?.toLocaleString('fr-FR')} €
                </p>
              </div>
              <div className="p-3 rounded-xl bg-amber-50">
                <Clock className="h-5 w-5 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Relances ce mois</p>
                <p className="text-2xl font-bold text-blue-600">
                  {stats?.this_month || 0}
                </p>
              </div>
              <div className="p-3 rounded-xl bg-blue-50">
                <Mail className="h-5 w-5 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Taux de succès</p>
                <p className="text-2xl font-bold text-emerald-600">
                  {stats?.success_rate || 0}%
                </p>
              </div>
              <div className="p-3 rounded-xl bg-emerald-50">
                <CheckCircle className="h-5 w-5 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Configuration */}
      <Card className="border-slate-200">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-violet-100">
              <Settings className="h-5 w-5 text-violet-600" />
            </div>
            <div>
              <CardTitle>Configuration des relances</CardTitle>
              <CardDescription>Définissez quand envoyer les rappels automatiques</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Activation */}
          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
            <div>
              <p className="font-medium text-slate-800">Relances automatiques</p>
              <p className="text-sm text-slate-500">Envoyer automatiquement des rappels aux clients</p>
            </div>
            <Switch
              checked={config?.enabled || false}
              onCheckedChange={(checked) => setConfig({...config, enabled: checked})}
              data-testid="auto-reminders-switch"
            />
          </div>

          {/* Email */}
          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
            <div>
              <p className="font-medium text-slate-800">Notification par email</p>
              <p className="text-sm text-slate-500">Envoyer les rappels par email au client</p>
            </div>
            <Switch
              checked={config?.send_email || false}
              onCheckedChange={(checked) => setConfig({...config, send_email: checked})}
            />
          </div>

          {/* Schedule */}
          <div className="space-y-4">
            <div>
              <p className="font-medium text-slate-800 mb-2">Planning des relances</p>
              <p className="text-sm text-slate-500">Définissez à quels jours après l'échéance envoyer les rappels</p>
            </div>
            
            <div className="flex flex-wrap gap-2">
              {schedule.map((day, index) => (
                <Badge 
                  key={day} 
                  variant="outline" 
                  className="px-4 py-2 text-base flex items-center gap-2 bg-white"
                >
                  <Calendar className="h-4 w-4 text-blue-500" />
                  J+{day}
                  <span className="text-xs text-slate-400">
                    ({index === 0 ? '1er rappel' : index === 1 ? '2e rappel' : '3e rappel'})
                  </span>
                  <button 
                    onClick={() => removeScheduleDay(day)}
                    className="ml-1 text-slate-400 hover:text-red-500"
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
            </div>

            <div className="flex gap-2">
              <Input
                type="number"
                placeholder="Jours après échéance"
                value={newDay}
                onChange={(e) => setNewDay(e.target.value)}
                className="w-48"
                min="1"
              />
              <Button variant="outline" onClick={addScheduleDay} disabled={!newDay}>
                <Plus className="h-4 w-4 mr-1" />
                Ajouter
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Factures en retard */}
      <Card className="border-slate-200">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-red-100">
                <AlertCircle className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <CardTitle>Factures en retard</CardTitle>
                <CardDescription>Factures nécessitant une relance</CardDescription>
              </div>
            </div>
            <Badge variant="outline" className="text-red-600 border-red-200">
              {overdueInvoices.length} facture(s)
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          {overdueInvoices.length > 0 ? (
            <div className="divide-y divide-slate-100">
              {overdueInvoices.map((invoice) => (
                <div 
                  key={invoice.id} 
                  className="flex items-center justify-between py-4"
                  data-testid={`overdue-invoice-${invoice.invoice_number}`}
                >
                  <div className="flex items-center gap-4">
                    <div className="p-2 rounded-lg bg-slate-100">
                      <Clock className="h-5 w-5 text-slate-600" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-slate-800">{invoice.invoice_number}</p>
                        <Badge 
                          className={
                            invoice.days_overdue > 30 ? 'bg-red-100 text-red-700' :
                            invoice.days_overdue > 14 ? 'bg-amber-100 text-amber-700' :
                            'bg-yellow-100 text-yellow-700'
                          }
                        >
                          +{invoice.days_overdue} jours
                        </Badge>
                        {invoice.reminder_count > 0 && (
                          <Badge variant="outline" className="text-blue-600">
                            {invoice.reminder_count} relance(s)
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-slate-500">{invoice.client_name}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="font-semibold text-slate-800">
                        {invoice.grand_total?.toLocaleString('fr-FR')} €
                      </p>
                      <p className="text-xs text-slate-500">
                        Échéance: {new Date(invoice.due_date).toLocaleDateString('fr-FR')}
                      </p>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => sendReminder(invoice.id, getReminderLabel(invoice.reminder_count))}
                      disabled={sendingReminder === invoice.id}
                      className="bg-blue-600 hover:bg-blue-700"
                      data-testid={`send-reminder-${invoice.invoice_number}`}
                    >
                      {sendingReminder === invoice.id ? (
                        <RefreshCw className="h-4 w-4 animate-spin" />
                      ) : (
                        <>
                          <Send className="h-4 w-4 mr-1" />
                          Relancer
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="h-8 w-8 text-emerald-500" />
              </div>
              <p className="text-slate-600 font-medium">Aucune facture en retard</p>
              <p className="text-sm text-slate-500 mt-1">Toutes vos factures sont à jour</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default RemindersPage;
